/**
 * Push notification registration for Capacitor native builds.
 *
 * On native (Android/iOS), this:
 *   1. Requests notification permission from the OS.
 *   2. Registers with FCM/APNs via the Capacitor PushNotifications plugin.
 *   3. Sends the device token to the saas-core backend so the motivator
 *      service can target the device for habit nudges.
 *   4. Installs listeners for foreground notifications and taps.
 *
 * On web, this is a no-op.
 */

import { Capacitor } from '@capacitor/core'
import { apiFetch } from '../config/api'
import { BRAND } from '../config/brand'

/**
 * Key under which we cache the current FCM device token in
 * `@capacitor/preferences`. The place-nudge fan-out reads this so the
 * phone can pass its own token as `origin_token` and the server skips
 * pushing a duplicate back to the device that already fired the local
 * notification.
 */
export const FCM_TOKEN_PREF_KEY = 'horton.fcm_token'

// `@capacitor/push-notifications` is only installed for native builds.
// Use a dynamic import so a missing module on web doesn't break the bundle.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let PushNotifications: any = null

// Same deal for @capacitor/preferences — guarded dynamic import.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let Preferences: any = null

async function loadPreferencesPlugin(): Promise<boolean> {
  if (Preferences) return true
  try {
    // @ts-ignore — optional native-only dependency
    const mod = await import('@capacitor/preferences')
    Preferences = mod.Preferences
    return true
  } catch (err) {
    console.warn('[push] @capacitor/preferences not available', err)
    return false
  }
}

async function cacheFcmToken(value: string): Promise<void> {
  const loaded = await loadPreferencesPlugin()
  if (!loaded || !Preferences) return
  try {
    await Preferences.set({ key: FCM_TOKEN_PREF_KEY, value })
  } catch (err) {
    console.warn('[push] cache fcm token failed', err)
  }
}

export async function getCachedFcmToken(): Promise<string | null> {
  const loaded = await loadPreferencesPlugin()
  if (!loaded || !Preferences) return null
  try {
    const { value } = await Preferences.get({ key: FCM_TOKEN_PREF_KEY })
    return typeof value === 'string' && value.length > 0 ? value : null
  } catch {
    return null
  }
}

async function loadPushPlugin(): Promise<boolean> {
  if (PushNotifications) return true
  try {
    // @ts-ignore — optional native-only dependency
    const mod = await import('@capacitor/push-notifications')
    PushNotifications = mod.PushNotifications
    return true
  } catch (err) {
    console.warn('[push] @capacitor/push-notifications not available', err)
    return false
  }
}

export async function initPushNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  const loaded = await loadPushPlugin()
  if (!loaded) return

  try {
    const permission = await PushNotifications.requestPermissions()
    if (permission.receive !== 'granted') {
      console.info('[push] permission not granted:', permission.receive)
      return
    }

    await PushNotifications.register()

    PushNotifications.addListener('registration', async (token: { value: string }) => {
      // Cache the token locally so the place-nudge flow can pass it back as
      // `origin_token` (prevents the server from echoing a push to the
      // device that already fired its local notification).
      void cacheFcmToken(token.value)

      try {
        const resp = await apiFetch('/api/push/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: token.value,
            platform: Capacitor.getPlatform(),
          }),
        })
        if (!resp.ok) {
          console.warn('[push] register failed', resp.status, await resp.text().catch(() => ''))
        }
      } catch (err) {
        console.warn('[push] register request error', err)
      }
    })

    PushNotifications.addListener('registrationError', (err: unknown) => {
      console.warn('[push] registrationError', err)
    })

    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: { title?: string; body?: string; data?: Record<string, unknown> }) => {
        console.info('[push] received (foreground):', notification.title, notification.body)
        // Foreground pushes don't hit the system tray — the APP is the
        // alert surface. A reminder firing plays the yanamas signature
        // sound (same motif as the notification channel) so it is
        // unmistakably yanamas, screen on or off the tray (owner ask
        // 2026-08-25).
        if ((notification.data as Record<string, unknown> | undefined)?.type === 'reminder_firing') {
          try {
            const alert = new Audio(`/sounds/${BRAND.assistant}_alert.wav`)
            alert.volume = 1.0
            void alert.play().catch(() => undefined)
          } catch { /* audio unavailable */ }
        }
      },
    )

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async (action: { notification: { data?: Record<string, unknown> } }) => {
        const data = action.notification?.data || {}
        const type = (data.type ?? data.kind) as string | undefined

        if (type === 'habit_occurrence') {
          const oid = data.habit_occurrence_id as string | undefined
          if (oid) {
            window.location.href = `/habits/occurrences/${encodeURIComponent(oid)}`
            return
          }
        }

        // Roadblocks are no longer a separate thing to open (owner 2026-09-06).
        // A follow-up push now lands on the item it is in the way of, if the
        // payload names one; otherwise on the task list.
        if (type === 'roadblock_followup') {
          const t = data.item_type as string | undefined
          const i = data.item_id as string | number | undefined
          window.location.href = t && i ? `/${t}s/${encodeURIComponent(String(i))}` : '/tasks'
          return
        }

        if (type === 'with_you') {
          try {
            const resp = await apiFetch('/api/with-you/session/start', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                entry_source: (data.with_you_session_source as string) || 'push',
                notification_id: data.notification_id as string | undefined,
              }),
            })
            if (resp.ok) {
              const json = await resp.json()
              const chatId = json.chat_session_id || ''
              const url = chatId
                ? `/horton-ai?with_you=true&chat_session_id=${encodeURIComponent(chatId)}`
                : `/horton-ai?with_you=true`
              window.location.href = url
              return
            }
          } catch (err) {
            console.warn('[push] with_you session start failed', err)
          }
          window.location.href = '/horton-ai?with_you=true'
          return
        }

        const route = data.route as string | undefined
        if (route) {
          window.location.href = route
        }
      },
    )
  } catch (err) {
    console.warn('[push] init error', err)
  }
}

export async function unregisterPushNotifications(token?: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    await apiFetch('/api/push/unregister', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
  } catch (err) {
    console.warn('[push] unregister failed', err)
  }
}
